import React from "react";
import styles from "./style.module.scss";
import logo from "../../../assets/images/mashie-shield-with-text-white.png";
import { Label } from "semantic-ui-react";
import { Link } from "react-router-dom";
import DeprecatedBanner from "./v2/utils/DeprecatedBanner";
import APIVersionDropdown from "./v2/utils/APIVersionDropdown";

const Endpoint = ({ type, location }) => {
    let colour = "green"; // for GET
    switch (type) {
        case "POST":
            colour = "blue";
            break;
        case "PUT":
            colour = "purple";
            break;
        case "PATCH":
            colour = "yellow";
            break;
        case "DELETE":
            colour = "red";
            break;
        default:
            colour = "green";
    }

    return (
        <h3>
            <Label size='large' color={colour}>
                {type}
            </Label>
            <span className={styles.endpoint}>{location}</span>
        </h3>
    );
};

export default function Index() {
    return (
        <div className='app events' style={{ paddingBottom: "100px" }}>
            <div className={styles.fullHeader} alt='event'>
                <img className={styles.entityLogo} src={logo} alt='logo' />
                <h1 className={styles.headerText}>
                    Golf Club Documentation (v1)
                </h1>
            </div>
            <div className='container'>
                <APIVersionDropdown version='v1' />
                <div className='row mt-5'>
                    <div className='col-12'>
                        <h2>About this API</h2>
                        <p>
                            This API allows you to find golf clubs and their
                            details
                        </p>
                    </div>
                </div>
                <div className='row mt-5'>
                    <div className='col-12'>
                        <h2>Endpoints</h2>

                        <Endpoint type='GET' location='/clubs' />
                        <p>
                            This endpoint returns a paginated list of all golf
                            clubs the passed member can access. The information
                            returned is minimal to prevent page loading times
                            from becoming too long. We recommend using a "show
                            more" modal and utilising the "summary" endpoint
                            listed below to get more information, or passing
                            directly to the main club data endpoint.
                        </p>
                        <h3>Query parameters</h3>
                        <p>
                            <Label>member</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <p className='mt-1'>
                            <Label>page</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the page number for the list of results.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>limit</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                Sets the number of records to return per page.
                                Defaults to 25.
                            </span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>
                            {`{ "data": [Club, ...], "total": 147 }`}
                        </pre>

                        <div className={styles.spacer}></div>
                        <Endpoint type='GET' location='/clubs/:id' />
                        <p>
                            This endpoint returns all information about the
                            passed golf club. This is best used when displaying
                            the club page only. Do not call this endpoint
                            repeatedly to get information about all clubs. Use
                            the summary endpoint instead.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>
                                The id of the golf club.
                            </span>
                        </p>
                        <h3>Query parameters</h3>
                        <p>
                            <Label>member</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>{`{ "data": Club }`}</pre>
                        <h3>Additional information</h3>
                        <p>
                            <Label>images</Label>
                            <Label basic className='ml-1'>
                                Array
                            </Label>
                            <span className='ml-3'>
                                Can be accessed at the following URL:
                                BASE_IMAGE_URL/content/clubs/images/:image
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>unavailable</Label>
                            <Label basic className='ml-1'>
                                Array
                            </Label>
                            <span className='ml-3'>
                                An array of dates with an optional "from" and
                                "to". If the "from" is not set, the club cannot
                                be booked until the "to" date at the earliest.
                                If the "to" date is not set, the club cannot be
                                booked after the "from" date. If both "to" and
                                "from" are set, the club is not bookable between
                                those two dates. The dates given are inclusive
                                of the dates that are not bookable.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>bookingLeadTime</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                The minimum number of days in the future we can
                                accept bookings at this club. The days are
                                inclusive of today (If today was the 1st of May
                                and there was a 4 day lead time, the first day
                                you would be able to book would be the 5th of
                                May).
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>bookingMaxTime</Label>
                            <Label basic className='ml-1'>
                                Int
                            </Label>
                            <span className='ml-3'>
                                The maximum number of days in the future we can
                                accept bookings at this club. The days are
                                inclusive of today (If today was the 1st of May
                                and there was a 14 day maximum time, the last
                                day you would be able to book would be the 14th
                                of May).
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.greenFees[season]</Label>
                            <Label basic className='ml-1'>
                                Summer / Winter
                            </Label>
                            <span className='ml-3'>
                                Summer runs from the 1st of April to the 31st of
                                October. Winter runs from the 1st of November to
                                the 31st of March.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.greenFees[season].poa</Label>
                            <Label basic className='ml-1'>
                                Boolean
                            </Label>
                            <span className='ml-3'>
                                A flag to tell if the season price is POA.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.greenFees[season].highest</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A value of the highest price a player would be
                                expected to pay at this course for the season if
                                not using our service. This can be used to
                                calculate discounts and comparative prices from
                                RRP.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.greenFees[season].lowest</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A helper value of the lowest price a player
                                would be expected to pay at this course for the
                                season from the prices we offer.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.greenFees[season][day][time]</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A value the player will be expected to pay for a
                                specific time of day (AM / PM (00:00-11:59 &
                                12:00-23:59)), on a specific day of the week in
                                the current season. If the value is "null" and
                                the "poa" value for the season is "true" you
                                should display POA. If the value is "null" and
                                the "poa" value for the season is "false" the
                                player is not allowed to book at that time.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.price.poa</Label>
                            <Label basic className='ml-1'>
                                Boolean
                            </Label>
                            <span className='ml-3'>
                                A helper flag to tell if the current seasonal
                                price for the current day of the week is POA. If
                                this is false and "courses.price.price" is null,
                                the course is not bookable today.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses.price.price</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A helper value to tell you the current seasonal
                                price for the current day of the week. If this
                                is null and "courses.price.poa" is false, the
                                course is not bookable today.
                            </span>
                        </p>
                        <div className={styles.spacer}></div>

                        <Endpoint type='GET' location='/clubs/:id/summary' />
                        <p>
                            This endpoint returns summary information about the
                            passed golf club. This is used as a bridge between
                            the clubs list endpoint and the main data endpoint
                            for clubs to get more information to display, but
                            not too much to overburden.
                        </p>
                        <h3>Path parameters</h3>
                        <p>
                            <Label>id</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>
                                The id of the golf club.
                            </span>
                        </p>
                        <h3>Query parameters</h3>
                        <p>
                            <Label>member</Label>
                            <Label basic className='ml-1'>
                                String
                            </Label>
                            <Label basic color='red' className='ml-1'>
                                Required
                            </Label>
                            <span className='ml-3'>The id of the member.</span>
                        </p>
                        <h3>Response</h3>
                        <Label basic color='green'>
                            200
                        </Label>
                        <span className='ml-2'>OK</span>
                        <pre className={styles.code}>{`{ "data": Club }`}</pre>
                        <h3>Additional information</h3>
                        <p>
                            <Label>images</Label>
                            <Label basic className='ml-1'>
                                Array
                            </Label>
                            <span className='ml-3'>
                                Can be accessed at the following URL:
                                BASE_IMAGE_URL/content/clubs/images/:image
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses[course].price.poa</Label>
                            <Label basic className='ml-1'>
                                Boolean
                            </Label>
                            <span className='ml-3'>
                                A flag to tell if the price for today's date is
                                POA.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses[course].price.price</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A value to tell you the price for today's date.
                                If this is null and "poa" is false, the course
                                is not bookable today for this member.
                            </span>
                        </p>
                        <p className='mt-1'>
                            <Label>courses[course].price.discount</Label>
                            <Label basic className='ml-1'>
                                Number / Null
                            </Label>
                            <span className='ml-3'>
                                A value to tell you the discount for today's
                                date from the headline rate for the current
                                season.
                            </span>
                        </p>
                        <div className={styles.spacer}></div>
                    </div>
                </div>
            </div>
            <DeprecatedBanner version='v1' />
        </div>
    );
}
